import { useState } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Center,
  Flex,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';

import {
  ArrowLeftOutline,
  Badge,
  BadgeStatus,
  DocumentDownloadOutline,
} from '@aslanpay/portal-common-lib';

import ReconciliationCompaniesTab from 'app/components/Reconciliations/ReconciliationCompaniesTab';
import ReconciliationFindingsTab from 'app/components/Reconciliations/ReconciliationFindingsTab';
import ReconciliationNotVerifiableTab from 'app/components/Reconciliations/ReconciliationNotVerifiableTab';
import ReconciliationProblemsTab from 'app/components/Reconciliations/ReconciliationProblemsTab';
import RefreshButton from 'app/components/Reconciliations/RefreshButton';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import EmptyState from 'app/components/UI/EmptyState';
import ProfileField from 'app/components/UI/ProfileField';

import {
  RECONCILIATION_COMPANY_STATUS_PARAM,
  RECONCILIATION_TAB_PARAM,
  ReconciliationProfileTabKey,
  ReconciliationProfileTabs,
  ReconciliationRunStatusValues,
} from 'app/constants/reconciliations';

import useDownloadReconciliationReport from 'app/hooks/useDownloadReconciliationReport';
import useFetchReconciliation, { RECONCILIATION_QUERY_KEY } from 'app/hooks/useFetchReconciliation';
import { RECONCILIATION_COMPANIES_QUERY_KEY } from 'app/hooks/useFetchReconciliationCompanies';
import { RECONCILIATION_FINDINGS_QUERY_KEY } from 'app/hooks/useFetchReconciliationFindings';
import useFetchReconciliationReport, {
  RECONCILIATION_REPORT_QUERY_KEY,
} from 'app/hooks/useFetchReconciliationReport';
import { UNVERIFIABLE_AGGREGATES_QUERY_KEY } from 'app/hooks/useFetchUnverifiableAggregates';

import { useManagingCompanyStore, useNavigationStore } from 'app/store';

import { ReconciliationCompanyStatus } from 'app/types/reconciliations';

import { getReconciliationsPath } from 'app/utils/routes';

import {
  formatDateTime,
  formatRunDuration,
  isActiveReconciliationRun,
} from 'app/utils/reconciliations';

const COMPANY_STATUSES: ReconciliationCompanyStatus[] = ['CHECKED', 'FAILED'];

/**
 * @description Builds the link that opens a tab of the run, optionally with the companies tab narrowed to a status.
 */
const tabLink = (tab: ReconciliationProfileTabKey, companyStatus?: ReconciliationCompanyStatus) => {
  const params = new URLSearchParams({ [RECONCILIATION_TAB_PARAM]: tab });
  if (companyStatus) {
    params.set(RECONCILIATION_COMPANY_STATUS_PARAM, companyStatus);
  }
  return `?${params.toString()}`;
};

/**
 * Shows one reconciliation run: its summary, whose counts open the matching tab, and tabs for its report, the problems
 * it found for the first time or resolved, the companies it covered and the aggregates it could not verify. In a
 * company's view every tab is limited to that company.
 */
function ReconciliationProfile() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all(
        [
          RECONCILIATION_QUERY_KEY,
          RECONCILIATION_REPORT_QUERY_KEY,
          RECONCILIATION_FINDINGS_QUERY_KEY,
          RECONCILIATION_COMPANIES_QUERY_KEY,
          UNVERIFIABLE_AGGREGATES_QUERY_KEY,
        ].map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
      );
    } finally {
      setIsRefreshing(false);
    }
  };
  const { reconciliationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const company = useManagingCompanyStore((state) => state.company);
  const view = useNavigationStore((state) => state.view);
  const companyId = view === 'company' ? company?.id : undefined;

  const tabKey = searchParams.get(RECONCILIATION_TAB_PARAM);
  const tabIndex = Math.max(
    ReconciliationProfileTabs.findIndex((tab) => tab.key === tabKey),
    0
  );
  const companyStatusParam = searchParams.get(RECONCILIATION_COMPANY_STATUS_PARAM);
  const companyStatus = COMPANY_STATUSES.find((status) => status === companyStatusParam);

  const { data: run, isLoading: isLoadingRun } = useFetchReconciliation(reconciliationId);
  const isRunning = isActiveReconciliationRun(run);
  const { data: report, isLoading: isLoadingReport } = useFetchReconciliationReport(
    reconciliationId,
    companyId,
    Boolean(run?.reportAvailable)
  );
  const { mutate: downloadReport, isPending: isDownloading } = useDownloadReconciliationReport();

  const handleTabChange = (index: number) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set(RECONCILIATION_TAB_PARAM, ReconciliationProfileTabs[index].key);
      return params;
    });
  };

  const handleCompanyStatusChange = (status?: ReconciliationCompanyStatus) => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      params.set(RECONCILIATION_TAB_PARAM, 'companies');
      if (status) {
        params.set(RECONCILIATION_COMPANY_STATUS_PARAM, status);
      } else {
        params.delete(RECONCILIATION_COMPANY_STATUS_PARAM);
      }
      return params;
    });
  };

  if (isLoadingRun) {
    return (
      <AuthenticatedPageWrapper title="Reconciliation">
        <Center w="full" h="full" flexGrow={1} data-testid="reconciliation-loading">
          <Spinner color="brand.purple" size="xl" />
        </Center>
      </AuthenticatedPageWrapper>
    );
  }

  if (!run || !reconciliationId) {
    return (
      <AuthenticatedPageWrapper title="Reconciliation">
        <EmptyState text="This reconciliation could not be found" />
      </AuthenticatedPageWrapper>
    );
  }

  const status = ReconciliationRunStatusValues[run.status];
  const hasReport = run.reportAvailable && report !== null;
  const detailsAvailable = Boolean(run.detailsAvailable);

  return (
    <AuthenticatedPageWrapper title="Reconciliation">
      <Button
        my="10px"
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={() => navigate(getReconciliationsPath())}
      >
        Back to reconciliations
      </Button>

      <Flex align="center" justify="space-between" gap="20px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Reconciliation {formatDateTime(run.startedAt ?? run.queuedAt)}
        </Text>
        <Flex gap="12px" flexShrink={0}>
          <RefreshButton
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            testId="refresh-reconciliation-button"
          />
          <Button
            leftIcon={<DocumentDownloadOutline boxSize="20px" />}
            size="sm"
            isDisabled={!hasReport}
            isLoading={isDownloading}
            onClick={() => downloadReport({ id: reconciliationId, companyId })}
            data-testid="download-reconciliation-report-button"
          >
            Download report
          </Button>
        </Flex>
      </Flex>

      <SimpleGrid
        mt="20px"
        w="full"
        columns={{ base: 1, md: 3, xl: 5 }}
        spacing="20px"
        p="20px"
        border="1px solid"
        borderColor="brand.lightGray1"
        borderRadius="10px"
        data-testid="reconciliation-summary"
      >
        <Flex gap="5px" flexDir="column" w="full">
          <Text color="brand.black" textStyle="extraSmallRegular">
            Status
          </Text>
          <Flex minH="52px" align="center">
            <Badge
              value={status?.value ?? run.status}
              status={(status?.color ?? 'created') as BadgeStatus}
            />
          </Flex>
        </Flex>
        <ProfileField label="Reconciliation ID" value={run.id} copyToClipboard />
        <ProfileField label="Triggered by" value={run.automated ? 'Nightly run' : 'An admin'} />
        <ProfileField label="Queued" value={formatDateTime(run.queuedAt)} />
        <ProfileField label="Started" value={formatDateTime(run.startedAt)} />
        <ProfileField label="Finished" value={formatDateTime(run.completedAt)} />
        <ProfileField label="Duration" value={formatRunDuration(run)} />
        <ProfileField
          label="Window"
          value={`${formatDateTime(run.windowStart)} to ${formatDateTime(run.windowEnd)}`}
        />
        <ProfileField
          label="Companies checked"
          value={String(run.companiesChecked)}
          link={tabLink('companies', 'CHECKED')}
        />
        <ProfileField
          label="Companies failed"
          value={String(run.companiesFailed)}
          link={tabLink('companies', 'FAILED')}
        />
        <ProfileField label="Aggregates checked" value={String(run.aggregatesChecked)} />
        <ProfileField
          label="Aggregates not verifiable"
          value={String(run.aggregatesNotVerifiable)}
          link={tabLink('not-verifiable')}
        />
        <ProfileField label="Transactions checked" value={String(run.transactionsChecked)} />
        <ProfileField
          label="Problems found"
          value={String(run.findingsCount)}
          link={tabLink('problems')}
        />
        <ProfileField
          label="New problems"
          value={String(run.newFindingsCount)}
          link={tabLink('new')}
        />
        <ProfileField
          label="Resolved problems"
          value={String(run.resolvedFindingsCount)}
          link={tabLink('resolved')}
        />
        <ProfileField
          label="Moved to past, never fixed"
          value={String(run.lapsedFindingsCount ?? 0)}
          link={tabLink('past')}
        />
      </SimpleGrid>

      {run.error && (
        <Flex
          mt="20px"
          p="16px"
          borderRadius="10px"
          bgColor="red.50"
          color="red.700"
          data-testid="reconciliation-error"
        >
          <Text>{run.error}</Text>
        </Flex>
      )}

      <Tabs mt="30px" w="full" index={tabIndex} onChange={handleTabChange} isLazy>
        <TabList>
          {ReconciliationProfileTabs.map((tab) => (
            <Tab key={tab.key} data-testid={`reconciliation-tab-${tab.key}`}>
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt="24px">
            <ReconciliationProblemsTab
              rows={report?.rows ?? []}
              isRunning={isRunning}
              hasReport={hasReport}
              isLoadingReport={isLoadingReport}
              showCompanyColumn={view === 'global'}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationFindingsTab
              reconciliationId={reconciliationId}
              mode="new"
              companyId={companyId}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationFindingsTab
              reconciliationId={reconciliationId}
              mode="resolved"
              companyId={companyId}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationFindingsTab
              reconciliationId={reconciliationId}
              mode="past"
              companyId={companyId}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationFindingsTab
              reconciliationId={reconciliationId}
              mode="fixed"
              companyId={companyId}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationCompaniesTab
              reconciliationId={reconciliationId}
              companyId={companyId}
              status={companyStatus}
              onStatusChange={handleCompanyStatusChange}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
          <TabPanel px={0} pt="24px">
            <ReconciliationNotVerifiableTab
              reconciliationId={reconciliationId}
              companyId={companyId}
              isRunning={isRunning}
              detailsAvailable={detailsAvailable}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </AuthenticatedPageWrapper>
  );
}

export default ReconciliationProfile;
